export interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  isActive: boolean;
  role: string;
}

export interface WorkspaceSession {
  token: string;
  tenant: { id: string; name: string; slug: string };
  user: { id: string; email: string; name: string | null; role: string; isSuperAdmin?: boolean };
}

const TOKENS_KEY = "vms_tokens";
const WORKSPACES_KEY = "vms_workspaces";
const LAST_WORKSPACE_KEY = "vms_last_workspace";
const RECENT_WORKSPACES_KEY = "vms_recent_workspaces";

export function readTokenMap(): Record<string, string> {
  try {
    const value = JSON.parse(localStorage.getItem(TOKENS_KEY) || "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}

export function readWorkspaces(): WorkspaceSummary[] {
  try {
    const value = JSON.parse(localStorage.getItem(WORKSPACES_KEY) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function storeWorkspaces(workspaces: WorkspaceSummary[]) {
  localStorage.setItem(WORKSPACES_KEY, JSON.stringify(workspaces));
}

export function workspaceFromPath(pathname = window.location.pathname): WorkspaceSummary | null {
  const slug = pathname.match(/^\/w\/([^/]+)/)?.[1];
  return slug ? readWorkspaces().find((workspace) => workspace.slug === decodeURIComponent(slug)) ?? null : null;
}

export function tokenForWorkspace(workspaceId: string): string | null {
  return readTokenMap()[workspaceId] ?? null;
}

export function tokenForCurrentRoute(pathname = window.location.pathname): string | null {
  const workspace = workspaceFromPath(pathname);
  return workspace ? tokenForWorkspace(workspace.id) : null;
}

export function anyWorkspaceToken(): string | null {
  const tokens = readTokenMap();
  const lastWorkspace = localStorage.getItem(LAST_WORKSPACE_KEY);
  return (lastWorkspace && tokens[lastWorkspace]) || Object.values(tokens)[0] || null;
}

export function storeWorkspaceSession(session: WorkspaceSession) {
  const tokens = readTokenMap();
  tokens[session.tenant.id] = session.token;
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));

  const workspaces = readWorkspaces();
  const previous = workspaces.find((workspace) => workspace.id === session.tenant.id);
  const workspace: WorkspaceSummary = {
    id: session.tenant.id,
    name: session.tenant.name,
    slug: session.tenant.slug,
    logoUrl: previous?.logoUrl ?? null,
    isActive: previous?.isActive ?? true,
    role: session.user.role,
  };
  storeWorkspaces([...workspaces.filter((item) => item.id !== workspace.id), workspace]);
  markWorkspaceUsed(workspace.id);
}

export function removeWorkspaceToken(workspaceId: string) {
  const tokens = readTokenMap();
  delete tokens[workspaceId];
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
}

export function removeCurrentWorkspaceToken(): boolean {
  const workspace = workspaceFromPath();
  if (!workspace) return false;
  removeWorkspaceToken(workspace.id);
  return true;
}

export function clearWorkspaceSession() {
  localStorage.removeItem(TOKENS_KEY);
  localStorage.removeItem(WORKSPACES_KEY);
  localStorage.removeItem(LAST_WORKSPACE_KEY);
  localStorage.removeItem(RECENT_WORKSPACES_KEY);
  sessionStorage.removeItem("vms_selection_token");
  sessionStorage.removeItem("vms_selection_workspaces");
}

export function markWorkspaceUsed(workspaceId: string) {
  localStorage.setItem(LAST_WORKSPACE_KEY, workspaceId);
  const recent = readRecentWorkspaceIds().filter((id) => id !== workspaceId);
  localStorage.setItem(RECENT_WORKSPACES_KEY, JSON.stringify([workspaceId, ...recent].slice(0, 4)));
}

export function readRecentWorkspaceIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_WORKSPACES_KEY) || "[]");
  } catch {
    return [];
  }
}

export function lastWorkspace(): WorkspaceSummary | null {
  const id = localStorage.getItem(LAST_WORKSPACE_KEY);
  return id ? readWorkspaces().find((workspace) => workspace.id === id) ?? null : null;
}

export function workspacePath(path: string, pathname = window.location.pathname): string {
  const slug = pathname.match(/^\/w\/([^/]+)/)?.[1];
  return slug ? `/w/${slug}/${path.replace(/^\//, "")}` : path;
}
